import { FC } from "react";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { AvailablePaymentCurrency, formatCurrencyFromCents, getLightPalette } from "@simplyk/common";

import { useBurst } from "@/canvas/features/FormV2/contexts/burstContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { mergeSx } from "@/common/helpers/mergeSx";
import { unstyledButtonSx } from "@/common/helpers/unstyledButtonSx";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormAmountField } from "@/forms-design-system/FormAmountField";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTab, FormTabGroup } from "@/forms-design-system/FormTab";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ArrowLeft, ArrowRight, Flash, Success } from "@/icons/outlined";

// Light mode keeps this pair. Dark mode uses the theme's positive text and surface.
const CHECKOUT_BUTTON = getLightPalette(0).forms;

type BidMode = "single" | "auto";

export interface FormBidBlockProps {
  currentBidAmountInCents: number;
  currentBidderName?: string;
  hasBids?: boolean;
  minimumBidLabel: string;
  taxNotice?: string;
  currency: AvailablePaymentCurrency;
  bidMode?: BidMode;
  onBidModeChange?: (mode: BidMode) => void;
  bidAmountInCents: number | null;
  onBidAmountChange: (amountInCents: number | null) => void;
  inputError?: string;
  inputPlaceholder?: string;
  autoBidDescription?: string;
  onSubmit: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
  submitted?: boolean;
  disabled?: boolean;
  onEditBid?: () => void;
  onReturnToList?: () => void;
  onGoToCheckout?: () => void;
  bidBadge?: React.ReactNode;
  questions?: React.ReactNode;
  isEditing?: boolean;
  onCancelBid?: () => void;
  cancelLabel?: string;
  updateLabel?: string;
  alreadyOutbidHelper?: string;
}

export const FormBidBlock: FC<FormBidBlockProps> = (props) => {
  const theme = useTheme();

  if (!theme.palette.forms) {
    return null;
  }

  const isSubmitted = props.submitted;
  const isDisabled = props.disabled;
  const isDarkMode = theme.palette.metadata?.mode === "dark";

  return (
    <Box
      sx={{
        padding: 2,
        borderRadius: theme.spacing(1),
        width: "100%",
        boxSizing: "border-box",
        transition: "background-color 250ms ease, border-color 250ms ease",
        ...(isSubmitted
          ? {
              backgroundColor: theme.palette.forms.surface.positive,
              border: `1px solid ${theme.palette.forms.border.positive}`,
            }
          : {
              backgroundColor: isDarkMode ? "transparent" : theme.palette.forms.surface.supershy,
              border: `1px solid ${theme.palette.formsComponents?.divider.borderColor}`,
            }),
        ...(isDisabled && { opacity: 0.6, pointerEvents: "none" as const }),
      }}
    >
      <Stack gap={2}>
        {!isSubmitted && props.bidBadge}
        {isSubmitted ? <SubmittedContent {...props} /> : <IdleContent {...props} />}
      </Stack>
    </Box>
  );
};

const IdleContent: FC<FormBidBlockProps> = ({
  currentBidAmountInCents,
  currentBidderName,
  hasBids = true,
  minimumBidLabel,
  taxNotice,
  currency,
  bidMode = "single",
  onBidModeChange,
  bidAmountInCents,
  onBidAmountChange,
  inputError,
  inputPlaceholder,
  autoBidDescription,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  disabled = false,
  questions,
  isEditing = false,
  onCancelBid,
  cancelLabel,
  updateLabel,
  alreadyOutbidHelper,
}) => {
  const { isoLocale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const { burstFromElement } = useBurst();

  const formattedCurrentBid = formatCurrencyFromCents(currentBidAmountInCents, {
    isoLocale,
    currency,
  });

  const handleBidAmountChange = (amountInCents: number | null) => {
    if ((amountInCents ?? 0) > (bidAmountInCents ?? 0)) {
      const input = document.querySelector<HTMLElement>('[data-test="drawer-bid-input"]');
      burstFromElement(input, { align: "left" });
    }
    onBidAmountChange(amountInCents);
  };

  const tabs = (
    <FormTabGroup
      value={bidMode}
      aria-label={t("common", "a11y.bidType")}
      onChange={(_, newValue) => onBidModeChange?.(newValue as BidMode)}
    >
      <FormTab value="single" label={t("ticketing", "auctionV2.bidBlock.singleBid")} disabled={disabled} />
      <FormTab
        value="auto"
        label={t("ticketing", "auctionV2.bidBlock.autoBid")}
        startIcon={<Flash />}
        components={{ startIcon: { color: "currentColor", size: "small" } }}
        disabled={disabled}
      />
    </FormTabGroup>
  );

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <CurrentBidDisplay
          label={t("ticketing", hasBids ? "auctionV2.bidBlock.currentBid" : "auctionV2.startingBid")}
          formattedAmount={formattedCurrentBid}
          bidderName={hasBids ? currentBidderName : undefined}
          bidderPrefix={t("ticketing", "auctionV2.bidBlock.bidderPrefix")}
        />
        <Box sx={{ flexShrink: 0, flexBasis: isSmallScreen ? "100%" : "auto" }}>{tabs}</Box>
      </Stack>
      <Collapse in={bidMode === "auto" && Boolean(autoBidDescription)} timeout={250} unmountOnExit>
        <FormTypography variant="body2" vibe="quiet">
          {autoBidDescription}
        </FormTypography>
      </Collapse>
      <Stack gap={0.5}>
        <FormAmountField
          size="large"
          currency={currency}
          value={bidAmountInCents}
          onChange={handleBidAmountChange}
          placeholder={inputPlaceholder ?? t("ticketing", "auctionV2.bidBlock.placeholder")}
          error={Boolean(inputError) || Boolean(alreadyOutbidHelper)}
          helperText={inputError ?? alreadyOutbidHelper}
          disabled={disabled}
          inputProps={{ "aria-label": t("common", "a11y.bidAmount") }}
          data-test="drawer-bid-input"
        />
        <FormTypography variant="caption" vibe="quiet" sx={{ marginBlockStart: 0.25 }}>
          {minimumBidLabel}
        </FormTypography>
        {taxNotice && (
          <FormTypography variant="caption" vibe="quiet" data-test="drawer-bid-tax-notice">
            {taxNotice}
          </FormTypography>
        )}
      </Stack>
      {questions}
      {isEditing && onCancelBid ? (
        <Stack direction="row" gap={1}>
          <FormButton variant="tonal" size="large" fullWidth onClick={onCancelBid} disabled={isSubmitting || disabled}>
            {cancelLabel ?? t("ticketing", "auctionV2.bidBlock.cancelBid")}
          </FormButton>
          <FormButton
            variant="solid"
            size="large"
            fullWidth
            onClick={onSubmit}
            isLoading={isSubmitting}
            disabled={disabled}
          >
            {updateLabel ?? t("ticketing", "auctionV2.bidBlock.updateBid")}
          </FormButton>
        </Stack>
      ) : (
        <FormButton
          variant="solid"
          size="large"
          fullWidth
          onClick={onSubmit}
          isLoading={isSubmitting}
          disabled={disabled}
          data-test="drawer-submit-bid"
        >
          {submitLabel}
        </FormButton>
      )}
    </Stack>
  );
};

interface CurrentBidDisplayProps {
  label: string;
  formattedAmount: string;
  bidderName?: string;
  bidderPrefix?: string;
}

const CurrentBidDisplay: FC<CurrentBidDisplayProps> = ({ label, formattedAmount, bidderName, bidderPrefix }) => (
  <Stack gap={0.25} sx={{ minWidth: 0 }}>
    <FormTypography variant="body2" vibe="moderate">
      {label}
    </FormTypography>
    <Stack direction="row" gap={1} alignItems="baseline" flexWrap="nowrap">
      <FormTypography variant="h5" vibe="intense" sx={{ whiteSpace: "nowrap" }}>
        {formattedAmount}
      </FormTypography>
      {bidderName && (
        <FormTypography
          variant="body2"
          vibe="quiet"
          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {bidderPrefix} {bidderName}
        </FormTypography>
      )}
    </Stack>
  </Stack>
);

const SubmittedContent: FC<FormBidBlockProps> = ({
  bidAmountInCents,
  bidMode = "single",
  currency,
  disabled = false,
  onEditBid,
  onReturnToList,
  onGoToCheckout,
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const forms = theme.palette.forms;

  if (!forms) {
    return null;
  }

  const positiveColor = forms.text.positive;
  const isDarkMode = theme.palette.metadata?.mode === "dark";
  // Dark fill is the lighter positive text. The label is the darker positive surface, matching the card.
  const checkoutFill = isDarkMode ? forms.text.positive : CHECKOUT_BUTTON.text.positive;
  const checkoutLabel = isDarkMode ? forms.surface.positive : CHECKOUT_BUTTON.text.reverse;

  const formattedBidAmount = formatCurrencyFromCents(bidAmountInCents ?? 0, { isoLocale, currency });

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={0.5}
        >
          <Stack direction="row" gap={1} alignItems="center" flex={1} minWidth={0}>
            <FormIcon size="medium" sx={{ color: positiveColor, flexShrink: 0 }}>
              <Success />
            </FormIcon>
            <FormTypography variant="subtitle1" color={positiveColor} sx={{ wordBreak: "break-word" }}>
              {t(
                "ticketing",
                bidMode === "auto"
                  ? "auctionV2.bidBlock.submittedMessageAutoBid"
                  : "auctionV2.bidBlock.submittedMessageSingleBid",
                { amount: formattedBidAmount }
              )}
            </FormTypography>
          </Stack>
          {onEditBid && (
            <Box component="button" type="button" onClick={onEditBid} sx={mergeSx(unstyledButtonSx, focusOutlineSx)}>
              <FormTypography
                variant="body2"
                component="span"
                color={positiveColor}
                sx={{ display: "block", textDecoration: "underline" }}
              >
                {t("ticketing", "auctionV2.bidBlock.editBid")}
              </FormTypography>
            </Box>
          )}
        </Stack>
        <FormTypography variant="body2" color={positiveColor}>
          {t("ticketing", "auctionV2.bidBlock.submittedNote")}
        </FormTypography>
      </Stack>

      {(onReturnToList || onGoToCheckout) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {onReturnToList && (
            <Stack
              component="button"
              type="button"
              direction="row"
              gap={0.5}
              alignItems="center"
              sx={mergeSx(unstyledButtonSx, focusOutlineSx)}
              onClick={onReturnToList}
              data-test="drawer-return-to-list"
            >
              <FormIcon size="small" sx={{ color: positiveColor }}>
                <ArrowLeft />
              </FormIcon>
              <FormTypography
                variant="body2"
                component="span"
                color={positiveColor}
                sx={{ textDecoration: "underline" }}
              >
                {t("ticketing", "auctionV2.bidBlock.returnToList")}
              </FormTypography>
            </Stack>
          )}
          {onGoToCheckout && (
            <FormButton
              variant="solid"
              endIcon={<ArrowRight />}
              onClick={onGoToCheckout}
              disabled={disabled}
              data-test="drawer-go-to-checkout"
              sx={{
                backgroundColor: checkoutFill,
                borderColor: checkoutFill,
                color: checkoutLabel,
                "&:hover": {
                  backgroundColor: checkoutFill,
                  borderColor: checkoutFill,
                  color: checkoutLabel,
                  filter: "brightness(0.92)",
                },
                "&:active, &:focus-visible": {
                  backgroundColor: checkoutFill,
                  borderColor: checkoutFill,
                  color: checkoutLabel,
                },
              }}
            >
              {t("ticketing", "auctionV2.bidBlock.goToCheckout")}
            </FormButton>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const focusOutlineSx: SxProps<Theme> = (theme) => ({
  borderRadius: theme.radius(1),
  outlineColor: "transparent",
  "&:focus-visible": {
    outline: `3px solid ${theme.palette.formsComponents?.global.focusOutlineColor}`,
  },
});
